import { ClosedCaption, ClosedCaptionOff } from "@mui/icons-material";
import DoneIcon from "@mui/icons-material/Done";
import { MenuItem, MenuList, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";

import { useAppState } from "../../../../hooks/useAppState";
import { getCaptionLanguages, sendClosedCaptionTelemetryEvent } from "../../../../util/captionLanguages";
import {
  ButtonActionsContainer,
  CaptionsButton,
  CaptionsButtonContainer,
  Container,
  StyledListItemIcon,
  StyledListItemText,
  StyledPaper,
} from "./ButtonClosedCaptions.elements";

interface ButtonClosedCaptionsProps {
  readonly captionsEnabled: boolean;
  readonly onClickCaptions: (value: boolean) => void;
  readonly captionLanguage: string;
  readonly setCaptionLanguage: (lang: string) => void;
  readonly availableCaptionLanguages?: { language: string; path?: string }[];
  readonly className?: string;
  readonly activeMenu: "captions" | "settings" | null;
  readonly setActiveMenu: (menu: "captions" | "settings" | null) => void;
}

export default function ButtonClosedCaptions({
  captionsEnabled,
  onClickCaptions,
  captionLanguage,
  setCaptionLanguage,
  availableCaptionLanguages,
  className,
  activeMenu,
  setActiveMenu,
}: ButtonClosedCaptionsProps): React.ReactElement {
  const { module } = useAppState();
  const [showMenu, setShowMenu] = useState(false);

  const captionPaths = module?.captionPaths;

  const sourceLanguages =
    availableCaptionLanguages && availableCaptionLanguages.length > 0
      ? availableCaptionLanguages
      : (captionPaths ?? [{ language: "en", path: "/video" }]);
  const captionLanguages = getCaptionLanguages(sourceLanguages);
  useEffect(() => {
    if (activeMenu !== "captions" && showMenu) {
      setShowMenu(false);
    }
  }, [activeMenu, showMenu]);

  const handleClick = () => {
    if (activeMenu === "captions" && showMenu) {
      setShowMenu(false);
      setActiveMenu(null);
      return;
    }

    setShowMenu(true);
    setActiveMenu("captions");
  };

  const handleLanguageSelect = (value: string) => {
    onClickCaptions(value !== "off");
    setCaptionLanguage(value);
    setShowMenu(false);
    setActiveMenu(null);

    sendClosedCaptionTelemetryEvent(value);
  };

  return (
    <Container className={className}>
      <CaptionsButtonContainer>
        <Tooltip title="Closed captions (c)">
          <span>
            <CaptionsButton
              ariaLabel={`Closed captions, ${captionsEnabled ? "enabled" : "disabled"}`}
              icon={captionsEnabled ? <ClosedCaption /> : <ClosedCaptionOff />}
              FabProps={{ tabIndex: 1 }}
              onClick={handleClick}
              data-testid={`captions-button-${className}-${captionsEnabled ? "enabled" : "disabled"}`}
            />
          </span>
        </Tooltip>
        {showMenu && (
          <ButtonActionsContainer>
            <StyledPaper>
              <MenuList>
                {captionLanguages.map(lang => (
                  <MenuItem
                    key={lang.value}
                    onClick={() => handleLanguageSelect(lang.value)}
                    divider={lang.value === "off" && !captionsEnabled}
                    aria-label={`Closed captions, ${lang.value}`}
                    data-testid={`captions-menu-item-${lang.value}`}
                  >
                    <StyledListItemIcon>{lang.value === captionLanguage && <DoneIcon />}</StyledListItemIcon>
                    <StyledListItemText>{lang.display}</StyledListItemText>
                  </MenuItem>
                ))}
              </MenuList>
            </StyledPaper>
          </ButtonActionsContainer>
        )}
      </CaptionsButtonContainer>
    </Container>
  );
}
